import React, { createContext, useContext, useState, useEffect } from 'react';
import { featuresApi } from '../services/api';
import { FEATURES, KNOWN_PLATFORMS } from '../constants/features';

const FeatureToggleContext = createContext({
    features: {},
    loading: true,
    isFeatureEnabled: () => true,
    getAllowedPlatforms: () => KNOWN_PLATFORMS,
    isPlatformEnabled: () => true,
});

export const FeatureToggleProvider = ({ children }) => {
    const [features, setFeatures] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFeatures = async () => {
            try {
                const data = await featuresApi.getFeatures();
                setFeatures(data);
            } catch (err) {
                console.error("Failed to fetch feature toggles, defaulting to fail-open:", err);
                // Fail-open: all features enabled, all platforms allowed
                const fallback = Object.values(FEATURES).reduce((acc, feat) => {
                    acc[feat] = true;
                    return acc;
                }, {});
                fallback.allowed_platforms = {};
                setFeatures(fallback);
            } finally {
                setLoading(false);
            }
        };

        fetchFeatures();
    }, []);

    const isFeatureEnabled = (featureName) => {
        if (loading) return true;
        return features[featureName] !== false;
    };

    /**
     * Returns the list of allowed platform strings for a given feature.
     * Defaults to all KNOWN_PLATFORMS if not configured (fail-open).
     * e.g. getAllowedPlatforms('posting') → ['instagram', 'facebook']
     */
    const getAllowedPlatforms = (featureName) => {
        if (loading) return KNOWN_PLATFORMS;
        const platformMap = features.allowed_platforms || {};
        return platformMap[featureName] || KNOWN_PLATFORMS;
    };

    /**
     * Returns true if a specific platform is allowed within a feature.
     * e.g. isPlatformEnabled('posting', 'youtube') → false
     */
    const isPlatformEnabled = (featureName, platform) => {
        return getAllowedPlatforms(featureName).includes(platform);
    };

    return (
        <FeatureToggleContext.Provider value={{ features, loading, isFeatureEnabled, getAllowedPlatforms, isPlatformEnabled }}>
            {children}
        </FeatureToggleContext.Provider>
    );
};

export const useFeatureToggles = () => useContext(FeatureToggleContext);
